import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getCurrency } from '@travel/i18n';
import { ACQUIRED_STATUS } from '@travel/traveler-core/constants/salesPromotion';
import {
  DESKTOP_SCREEN,
  MOBILE_SCREEN,
  useDeviceType,
  useDialogHandler,
} from '@travel/traveler-core/hooks';
import { cx } from '@travel/utils';

import CouponSkeleton from 'components/CouponSkeleton';
import Dialog from 'components/Dialog';

import { acquiredCoupon, fetchProviderRatePlan } from 'store/providerRatePlan/actions';
import { fetchCouponDetails, resetCouponDetail } from 'store/salesPromotion/actions';
import { getMultipleCouponDetails } from 'store/salesPromotion/selectors';

import { OFFER_TYPE } from 'constants/salesPromotion';
import { Translate } from 'core/translate';
import useCurrencyChangeCallback from 'hooks/useCurrencyChangeCallback';
import type { SearchFormQuery } from 'ProviderList-Types';
import type { CombinedIncentive, SalesPromotion } from 'ProviderRatePlan-Types';

import { getRatePlanRequestBody } from '../../resolver';
import CouponBadge from './components/CouponBadge';
import CouponBadgeWithDiscount from './components/CouponBadgeWithDiscount';
import { CouponItem } from './components/CouponItem';

import styles from './planCouponItem.module.scss';

export type Props = {
  className?: string;
  /** String to define the current item id */
  itemId: string;
  /** String to define the current plan id */
  planId: string;
  /** String to define the current rate plan id */
  ratePlanId: string;
  /** Sales promotion to be displayed and consumed */
  salesPromotions: SalesPromotion[];
  /** Discount price & points to be displayed */
  combinedIncentive?: CombinedIncentive;
  /** Number of price after applied the coupon */
  priceAfterDiscount: number;
  /** Number of coupons applied to the item */
  noOfAppliedCoupons: number;
  /** Number of coupons available to be applied to the item */
  noOfAvailableCoupons: number;
  /** String to define the current provider id */
  pageIdentifier: string;
  query: SearchFormQuery;
  /** TIDP-3854 to maintain two designs of coupon badge */
  parentComponent?: 'ItemCard' | 'DetailDialog';
};

function PlanCouponItem(props: Props) {
  const { parentComponent = 'DetailDialog' } = props;
  const dispatch = useDispatch();

  const isPC = useDeviceType() === DESKTOP_SCREEN;
  const isSP = useDeviceType() === MOBILE_SCREEN;

  const { isOpen, onClose, onOpen } = useDialogHandler(false);
  const [isNewCouponAcquired, setIsNewCouponAcquired] = useState(false);

  const salesPromotionIds = props.salesPromotions?.map((sp) => sp.id);
  const couponDetails = useSelector(getMultipleCouponDetails(salesPromotionIds));

  const currency = useSelector(getCurrency);
  // if any coupon is not same with user currency, show annotation message
  const isSameWithCouponCurrency = couponDetails
    ?.map((coupon) => coupon?.localCurrency === currency)
    .reduce((prev, current) => prev && current, true);

  // when the coupons to be shown on dialog is only 1 coupon, show the detail
  const isDetailInitiallyOpen = !isSP || props.salesPromotions?.length === 1;

  const onCurrencyChange = useCallback(() => dispatch(resetCouponDetail()), [dispatch]);
  useCurrencyChangeCallback(onCurrencyChange);

  const handleCouponLinkSuccess = (salesPromotionId: string) => {
    dispatch(
      acquiredCoupon({
        salesPromotionId,
        itemId: props.itemId,
        ratePlanId: props.ratePlanId,
        planId: props.planId,
      }),
    );
    setIsNewCouponAcquired(true);
  };

  const onClickCouponBadge = useCallback(async () => {
    salesPromotionIds.forEach((salesPromotionId) => {
      dispatch(fetchCouponDetails(salesPromotionId));
    });
    onOpen();
  }, [dispatch, onOpen, salesPromotionIds]);

  const handleDialogClose = () => {
    // user may acquire 2 coupons in dialog
    // fetching will be done when dialog closes
    if (isNewCouponAcquired) {
      const coupon = props.query?.salesPromotionId
        ? couponDetails?.[
            props.salesPromotions.map((sp) => sp.id).indexOf(props.query.salesPromotionId)
          ]
        : undefined;
      dispatch(
        fetchProviderRatePlan(getRatePlanRequestBody(props.pageIdentifier, props.query, coupon)),
      );
    }
    onClose();
  };

  if (!props.salesPromotions?.length) {
    return null;
  }

  // if there is unacquired coupon, should be listed on top
  const flipCouponDisplay =
    props.salesPromotions?.length === 2 &&
    props.salesPromotions?.[0].acquiredStatus === ACQUIRED_STATUS.ACQUIRED_BY_USER &&
    props.salesPromotions?.[1].acquiredStatus === ACQUIRED_STATUS.NOT_ACQUIRED_BY_USER;

  const salesPromotionForDisplay = flipCouponDisplay
    ? [...props.salesPromotions]?.reverse()
    : props.salesPromotions;
  const couponDetailForDisplay = flipCouponDisplay
    ? [...(couponDetails || [])]?.reverse()
    : couponDetails;

  return (
    <>
      {/* TIDP-3854 scope of design update on coupon badge will not be applied to plan/room dialog */}
      {parentComponent === 'ItemCard' ? (
        <CouponBadge
          className={cx(props.className, styles.planItemCoupon)}
          combinedIncentive={props.combinedIncentive}
          noOfAppliedCoupons={props.noOfAppliedCoupons}
          noOfAvailableCoupons={props.noOfAvailableCoupons}
          onClick={onClickCouponBadge}
          priceAfterDiscount={props.priceAfterDiscount}
        />
      ) : (
        <CouponBadgeWithDiscount
          className={cx(props.className, styles.planItemCoupon)}
          combinedIncentive={props.combinedIncentive}
          noOfAppliedCoupons={props.noOfAppliedCoupons}
          noOfAvailableCoupons={props.noOfAvailableCoupons}
          onClick={onClickCouponBadge}
          priceAfterDiscount={props.priceAfterDiscount}
        />
      )}

      <Dialog
        isOpen={isOpen}
        isSEOContentIndexedOnHidden={true}
        onClose={handleDialogClose}
        title={isPC ? null : <Translate id="Provider_Info.Provider_Info.Coupon.Title" />}
        className={styles.couponItemDialog}
        contentClassName={styles.couponDetail}
        hasAnimation={true}
      >
        <div
          className={styles.contentWrapper}
          data-testid="providerInfo-ratePlanList-planCouponItem-dialogWrapper"
        >
          {isPC && (
            <h2 className={styles.couponTitle}>
              <Translate id="Provider_Info.Provider_Info.Coupon.Title" />
            </h2>
          )}
          <Translate
            className={styles.annotation}
            id="Coupon_Details.Price_Disclaimer"
            condition={() =>
              `status == ${isSameWithCouponCurrency ? 'same_currency' : 'different_currency'}`
            }
          />
          <div className={styles.couponItems}>
            {salesPromotionForDisplay?.map((salesPromotion, index) => {
              if (salesPromotion.offerType !== OFFER_TYPE.COUPON) {
                return null;
              }

              return couponDetailForDisplay?.[index] ? (
                <CouponItem
                  key={`couponItem_${index}`}
                  salesPromotionId={salesPromotion.id}
                  couponDetail={couponDetailForDisplay[index]}
                  isAcquired={salesPromotion.acquiredStatus === ACQUIRED_STATUS.ACQUIRED_BY_USER}
                  isDetailInitiallyOpen={isDetailInitiallyOpen}
                  handleCouponLinkSuccess={() => handleCouponLinkSuccess(salesPromotion.id)}
                  data-testid="providerInfo-planCouponItem-couponItem"
                />
              ) : (
                <CouponSkeleton
                  key={`couponSkeleton_${index}`}
                  isCouponAccordionOpen={isDetailInitiallyOpen}
                />
              );
            })}
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default PlanCouponItem;
